import React, { memo, useCallback, useEffect, useState } from "react";
import classNames from "classnames";

import StickyButton from "@/common/StickyButton";
import {
    logAnalyticsEvent,
    logPaltaAnalyticsEvent,
    logSingleSourceAnalyticsEvent,
} from "@/constants/amplitude";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { sendMetrics } from "@/constants/gtag";
import { QUESTIONS_IDS, QuestionTypes } from "@/constants/questionsIds";
import i18n from "@/localization/i18next";
import { HandleNext } from "@/types/common";
import { Question } from "@/types/question";
import { getCheckedItems } from "@/utils/getCheckedItems";

import useAnswers from "../../utils/useAnswers";

interface CheckboxImageLeftProps {
    question: Question<QuestionTypes.CheckboxImageLeft>;
    handleNext: HandleNext;
    isActive: boolean;
}

const CheckboxImageLeft = memo<CheckboxImageLeftProps>(({ question, handleNext, isActive }) => {
    const { answers } = useAnswers();
    const [value, setValue] = useState<string[]>([]);
    const [isPointerEventDisabled, setIsPointerEventDisabled] = useState(false);
    const isDisabled = !value.length;

    const toggleAnswer = useCallback(
        (id?: string) => {
            if (id) {
                const items = getCheckedItems(id, value);
                setValue(items);
            }
        },
        [value]
    );

    const handleContinue = useCallback(() => {
        setIsPointerEventDisabled(true);
        if (question.id === QUESTIONS_IDS.HEALTH_PROBLEMS) {
            logSingleSourceAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
            });
            logPaltaAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
                [WebEventProps.RESULT]: value,
            });
            sendMetrics(WebEventTypes.RESULT_EVENT, { [WebEventProps.SCREEN]: question.event });
        } else {
            logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
                [WebEventProps.RESULT]: value,
            });
            sendMetrics(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
                [WebEventProps.RESULT]: value,
            });
        }

        handleNext({ [question.id]: value });
    }, [handleNext, question, value]);

    useEffect(() => {
        if (isActive) {
            setIsPointerEventDisabled(false);
        }
    }, [isActive]);

    useEffect(() => {
        if (answers[question.id]) {
            setValue(answers[question.id] as string[]);
        }
    }, [question.id, answers]);

    return (
        <div className="flex h-full flex-col justify-between gap-10">
            <div className="grid gap-6">
                <div className="grid gap-4">
                    <div className="text-zing-24-32 font-semibold text-white female:text-black">
                        {question.title}
                    </div>
                    {question.subtitle && (
                        <div className="whitespace-pre-line text-zing-16-24 text-white female:text-black">
                            {question.subtitle}
                        </div>
                    )}
                </div>
                <div className="grid gap-4">
                    {question.emptyAnswer && (
                        <>
                            <div
                                className={classNames(
                                    "flex items-center gap-4 rounded-2xl p-1 text-white hover:cursor-pointer",
                                    {
                                        "bg-zing-cool-blue-600 female:bg-zing-warm-tangerine-400 female:text-white":
                                            question.emptyAnswer?.id &&
                                            value?.includes(question.emptyAnswer?.id),
                                        "bg-zing-black-200 female:bg-zing-warm-tangerine-100 female:text-black":
                                            question.emptyAnswer?.id &&
                                            !value?.includes(question.emptyAnswer?.id),
                                    }
                                )}
                                onClick={() => toggleAnswer(question.emptyAnswer?.id)}
                            >
                                <img
                                    src={question.emptyAnswer?.img}
                                    alt=""
                                    className={classNames({
                                        "h-14 w-14": QUESTIONS_IDS.HEALTH_PROBLEMS,
                                        "h-[86px] w-[86px]": question.isLargeTiles,
                                    })}
                                />
                                <span className="text-zing-16-22 font-semibold">
                                    {question.emptyAnswer?.text}
                                </span>
                            </div>

                            <div className="border-t border-zing-black-200 female:border-zing-neutral-cool-grey-200" />
                        </>
                    )}
                    <ul className="flex flex-col gap-2">
                        {question.answers.map((answer) => (
                            <li
                                className={classNames(
                                    "flex justify-between rounded-2xl p-1 pr-4 text-white hover:cursor-pointer",
                                    {
                                        "checkbox--checked bg-zing-cool-blue-600 female:bg-zing-warm-tangerine-400 female:text-white":
                                            value?.includes(answer.id),
                                        "bg-zing-black-200 female:bg-zing-warm-tangerine-100 female:text-black":
                                            !value?.includes(answer.id),
                                        "p-2": question.isLargeTiles,
                                    }
                                )}
                                key={answer.id}
                                onClick={() => toggleAnswer(answer.id)}
                            >
                                <div className="flex items-center gap-4">
                                    <img
                                        src={answer.img}
                                        alt=""
                                        className={classNames({
                                            "h-14 w-14 rounded-xl bg-white":
                                                QUESTIONS_IDS.HEALTH_PROBLEMS,
                                            "h-[86px] w-[86px]": question.isLargeTiles,
                                        })}
                                    />
                                    <span className="text-zing-16-22 font-semibold">
                                        {answer.text}
                                    </span>
                                </div>
                                <div
                                    className={classNames(
                                        "checkbox-circle order-4 h-6 w-6 flex-shrink-0 self-center rounded-full border-2 border-zing-gray-200 bg-black female:border-zing-warm-tangerine-300 female:bg-white",
                                        {
                                            "border-none": value?.includes(answer.id),
                                        }
                                    )}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <StickyButton
                onClick={handleContinue}
                isDisabled={isDisabled}
                className={classNames({
                    "pointer-events-none": isPointerEventDisabled,
                    "invisible opacity-0": isDisabled,
                })}
            >
                <span className="normal-case">{i18n.t("continue")}</span>
            </StickyButton>
        </div>
    );
});

export default CheckboxImageLeft;
